import type {
  LayoutVisibleRule,
  ReportLayoutConfig,
  ReportPageTabConfig,
  ReportSectionConfig,
  ReportSectionTabConfig,
} from '../models/layoutConfig.types'

// Pure port of v3 `layoutStore.setReportLayout` (layoutStore.ts:156–215): a workspace-data
// availability filter over one layout config. All schedule knowledge is injected so the
// filter stays unit-testable and the composable adapter owns the store coupling.

export interface LayoutAvailabilityQuery {
  reportId: number | undefined
  tabs: readonly string[]
  checkCols?: readonly string[] | undefined
  checkDimension?: { field: string; value: string[] } | undefined
}

export interface ResolveLayoutDeps {
  /**
   * Truthiness of v3 `checkScheduleTabs(reportId, tabs, null, checkCols, checkDimension)`:
   * the report's schedule carries any of the named tabs, narrowed by column/dimension checks.
   */
  isDataAvailable: (query: LayoutAvailabilityQuery) => boolean
  /** Workspace `ga4_key_event_mapping` attribute presence (v3 'Key Events' tab gate). */
  hasKeyEventMapping: boolean
}

function ruleMatches(rule: LayoutVisibleRule, deps: ResolveLayoutDeps): boolean {
  return deps.isDataAvailable({
    reportId: rule.reportId,
    tabs: rule.tabs,
    checkCols: rule.checkCols,
    checkDimension: rule.checkDimension,
  })
}

/** v3 `notValid` inversion: a matching rule can either show or HIDE its carrier. */
function passesVisibleRule(rule: LayoutVisibleRule | undefined, deps: ResolveLayoutDeps): boolean {
  if (!rule) return true
  const matches = ruleMatches(rule, deps)
  return rule.notValid ? !matches : matches
}

function resolveSectionTabs(
  section: ReportSectionConfig,
  deps: ResolveLayoutDeps,
): ReportSectionTabConfig[] {
  return section.tabs.filter((tab) => {
    // Section `reportId` wins over the inner tab's (v3 layoutStore.ts:182). A tab without
    // schedule targets has no data gate and stays (metadata-only stub tabs).
    const dataAvailable =
      !tab.tabs?.length ||
      deps.isDataAvailable({
        reportId: section.reportId ?? tab.reportId,
        tabs: tab.tabs,
        checkCols: tab.checkCols,
        checkDimension: tab.checkDimension,
      })
    if (!dataAvailable) return false
    return passesVisibleRule(tab.visibleRule, deps)
  })
}

/**
 * Filter one layout config down to what the selected workspace's schedules can serve.
 * Pure and non-mutating: emits shallow copies, drops empty sections and empty page tabs.
 */
export function resolveLayout(
  config: ReportLayoutConfig,
  deps: ResolveLayoutDeps,
): ReportLayoutConfig {
  if (!Array.isArray(config)) return []
  return config
    .filter((pageTab: ReportPageTabConfig) => {
      if (pageTab.requiresKeyEventMapping && !deps.hasKeyEventMapping) return false
      return passesVisibleRule(pageTab.visibleRule, deps)
    })
    .map((pageTab) => ({
      ...pageTab,
      sections: pageTab.sections
        .map((section) => ({ ...section, tabs: resolveSectionTabs(section, deps) }))
        .filter((section) => section.tabs.length > 0),
    }))
    .filter((pageTab) => pageTab.sections.length > 0)
}
